import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Shape of a single notification row returned by the API.
 * Matches the PostgreSQL `notification` table joined with actor details.
 */
export interface NotificationRow {
  id: number;
  user_id: number;
  actor_id: number;
  notification_type: string;
  entity_type: string | null;
  entity_id: number | null;
  related_entity_type: string | null;
  related_entity_id: number | null;
  request_id: number | null;
  request_status: string | null;
  requestor_type: string | null;
  requestor_id: number | null;
  requestee_type: string | null;
  requestee_id: number | null;
  is_read: boolean;
  properties: Record<string, unknown>;
  created_at: string;
  updated_at: string | null;
  actor_type: string;
  actor_name: string;
  actor_display_name: string;
  actor_properties_public: Record<string, unknown> | null;
  entity_name: string | null;
  entity_properties_public: Record<string, unknown> | null;
  requestor_name: string | null;
  requestor_properties_public: Record<string, unknown> | null;
  requestee_name: string | null;
  requestee_properties_public: Record<string, unknown> | null;
}

interface NotificationListPage {
  notifications: NotificationRow[];
  nextCursor: string | null;
  nextCursorId: number | null;
  hasMore: boolean;
}

interface UnreadCountResponse {
  count: number;
}

interface MarkReadResponse {
  count: number;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const notificationQueryKeys = {
  all: () => ["notifications"] as const,
  unreadCount: () => ["notifications", "unreadCount"] as const,
  list: () => ["notifications", "list"] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Fetch the current user's unread notification count.
 * WebSocket `notification:count` events keep this fresh between fetches.
 * A 5-minute safety-net refetch covers reconnection gaps.
 */
export function useNotificationUnreadCount(enabled = true) {
  return useQuery<UnreadCountResponse, Error>({
    queryKey: notificationQueryKeys.unreadCount(),
    queryFn: async () => {
      const response = await api.get<UnreadCountResponse>(
        apiRoutes.notifications.unreadCount
      );
      return response.data;
    },
    enabled,
    refetchInterval: enabled ? 5 * 60 * 1000 : false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

/**
 * Infinite-scroll notification list using cursor-based pagination.
 */
export function useNotificationList(enabled = true) {
  return useInfiniteQuery<NotificationListPage, Error>({
    queryKey: notificationQueryKeys.list(),
    queryFn: async ({ pageParam }) => {
      const params = pageParam as {
        cursor?: string;
        cursorId?: number;
      } | undefined;

      const searchParams = new URLSearchParams();
      searchParams.set("limit", "20");
      if (params?.cursor) {
        searchParams.set("cursor", params.cursor);
      }
      if (params?.cursorId) {
        searchParams.set("cursor_id", String(params.cursorId));
      }

      const response = await api.get<NotificationListPage>(
        `${apiRoutes.notifications.root}?${searchParams.toString()}`
      );
      return response.data;
    },
    initialPageParam: undefined as
      | { cursor?: string; cursorId?: number }
      | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.nextCursor
        ? { cursor: lastPage.nextCursor, cursorId: lastPage.nextCursorId ?? undefined }
        : undefined,
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

interface OptimisticNotificationContext {
  previousList: InfiniteData<NotificationListPage> | undefined;
  previousCount: UnreadCountResponse | undefined;
}

/**
 * Mark specific notifications as read. Optimistically updates the list cache
 * and decrements the unread badge count.
 */
export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation<MarkReadResponse, Error, number[], OptimisticNotificationContext>({
    mutationFn: async (notificationIds) => {
      const response = await api.patch<MarkReadResponse>(
        apiRoutes.notifications.markRead,
        { notification_ids: notificationIds }
      );
      return response.data;
    },
    onMutate: async (notificationIds) => {
      await queryClient.cancelQueries({ queryKey: notificationQueryKeys.list() });
      await queryClient.cancelQueries({ queryKey: notificationQueryKeys.unreadCount() });

      const previousList = queryClient.getQueryData<
        InfiniteData<NotificationListPage>
      >(notificationQueryKeys.list());
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(
        notificationQueryKeys.unreadCount()
      );

      const idsSet = new Set(notificationIds);
      let markedCount = 0;

      if (previousList) {
        queryClient.setQueryData<InfiniteData<NotificationListPage>>(
          notificationQueryKeys.list(),
          {
            ...previousList,
            pages: previousList.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((n) => {
                if (idsSet.has(n.id) && !n.is_read) {
                  markedCount++;
                  return { ...n, is_read: true };
                }
                return n;
              }),
            })),
          }
        );
      }

      if (previousCount && markedCount > 0) {
        queryClient.setQueryData<UnreadCountResponse>(
          notificationQueryKeys.unreadCount(),
          { count: Math.max(0, previousCount.count - markedCount) }
        );
      }

      return { previousList, previousCount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(notificationQueryKeys.list(), context.previousList);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationQueryKeys.unreadCount(), context.previousCount);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount() });
    },
  });
}

/**
 * Mark all notifications as read. Optimistically sets every cached row's
 * `is_read` to true and the badge count to 0.
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation<MarkReadResponse, Error, void, OptimisticNotificationContext>({
    mutationFn: async () => {
      const response = await api.patch<MarkReadResponse>(
        apiRoutes.notifications.markAllRead
      );
      return response.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationQueryKeys.list() });
      await queryClient.cancelQueries({ queryKey: notificationQueryKeys.unreadCount() });

      const previousList = queryClient.getQueryData<
        InfiniteData<NotificationListPage>
      >(notificationQueryKeys.list());
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(
        notificationQueryKeys.unreadCount()
      );

      if (previousList) {
        queryClient.setQueryData<InfiniteData<NotificationListPage>>(
          notificationQueryKeys.list(),
          {
            ...previousList,
            pages: previousList.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((n) => ({
                ...n,
                is_read: true,
              })),
            })),
          }
        );
      }

      queryClient.setQueryData<UnreadCountResponse>(
        notificationQueryKeys.unreadCount(),
        { count: 0 }
      );

      return { previousList, previousCount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(notificationQueryKeys.list(), context.previousList);
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationQueryKeys.unreadCount(), context.previousCount);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount() });
    },
  });
}

/**
 * Delete notifications by ID. Invalidates list and count caches after success.
 */
export function useDeleteNotifications() {
  const queryClient = useQueryClient();

  return useMutation<MarkReadResponse, Error, number[]>({
    mutationFn: async (notificationIds) => {
      const response = await api.delete<MarkReadResponse>(
        apiRoutes.notifications.root,
        { data: { notification_ids: notificationIds } }
      );
      return response.data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.list() });
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount() });
    },
  });
}
